/* Notifications, unread & the inbox (A3), real-only.

   Rides the sync stream and turns it into @mentions, per-room unread + read
   markers, "entered your room" notices, DND-aware delivery (Deep work / Break
   hold knocks and mute toasts), and a persistent inbox. No mock simulation. */
import { defineStore } from 'pinia'
import { watch } from 'vue'
import { matrix } from '@/lib/matrix'
import { sync } from '@/lib/sync'
import { useToasts } from '@/stores/toasts'
import { usePresence } from '@/stores/presence'
import { useFloor } from '@/stores/floor'
import { relativeAgo } from '@/lib/util'
import { playSfx } from '@/lib/sfx'
import { mentionsInclude } from '@/lib/mentions'
import { useMentions } from '@/stores/mentions'
import type { InboxItem } from '@/types/atrium'
import type { MatrixEvent, RoomMessageContent } from '@/types/matrix'

const READ_ACCOUNT = 'app.atrium.read'

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))
}
function reEsc(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

let inboxSeq = 0

interface NotifyState {
  floorTs: number
  lastRead: Record<string, number>
  unread: Record<string, number>
  mentioned: Record<string, boolean>
  inbox: InboxItem[]
  seen: Set<string>
  activeChat: string | null
  queuedKnocks: InboxItem[]
  inboxOpen: boolean
  inited: boolean
  saveTimer: ReturnType<typeof setTimeout> | null
}

export const useNotify = defineStore('notify', {
  state: (): NotifyState => ({
    floorTs: Date.now(),
    lastRead: {},
    unread: {},
    mentioned: {},
    inbox: [],
    seen: new Set(),
    activeChat: null,
    queuedKnocks: [],
    inboxOpen: false,
    inited: false,
    saveTimer: null,
  }),

  getters: {
    unreadFor: (s) => (roomId: string): number => s.unread[roomId] || 0,
    isMentioned: (s) => (roomId: string): boolean => !!s.mentioned[roomId],
    inboxUnread: (s): number => s.inbox.filter((it) => !it.read).length,
    totalUnread: (s): number => Object.keys(s.unread).reduce((a, k) => a + s.unread[k], 0),
  },

  actions: {
    init() {
      if (this.inited) return
      this.inited = true

      sync.on('toDevice', (ev) => this.onSignal(ev))
      // Protocol-level room knocks (knock_restricted team rooms): membership
      // events in rooms we're in.
      sync.on('member', (roomId, ev) => this.onMemberEvent(roomId, ev))

      // DND: flush held knocks when you come back.
      const presence = usePresence()
      let lastDnd = presence.isDnd
      watch(
        () => presence.isDnd,
        (nowDnd) => {
          if (lastDnd && !nowDnd && this.queuedKnocks.length) {
            const n = this.queuedKnocks.length
            this.queuedKnocks = []
            useToasts().push({
              icon: 'knock',
              title: `${n} knock${n > 1 ? 's' : ''} while you were heads-down`,
              text: 'They’re waiting in your inbox.',
              action: 'Open inbox',
              ms: 7000,
              onAction: () => this.open(),
            })
          }
          lastDnd = nowDnd
        },
      )

    },

    async boot() {
      try {
        const data = await matrix.getAccountData<{ rooms?: Record<string, number> }>(READ_ACCOUNT)
        if (data?.rooms) this.lastRead = { ...data.rooms }
      } catch { /* none yet */ }
    },

    /* ---- identity / mention matching ---- */
    myNames(): string[] {
      const id = matrix.userId()
      if (!id) return []
      const local = id.replace(/^@/, '').split(':')[0]
      const me = useFloor().you
      return [...new Set([local, me?.name, me?.short].filter((n): n is string => !!n && n.length >= 2))]
    },
    isMention(text: string, content?: RoomMessageContent): boolean {
      const myId = matrix.userId() || ''
      const mentions = content?.['m.mentions']
      if (myId && mentions?.user_ids?.includes(myId)) return true
      // An explicit @[Name](mxid) token aimed at me is a mention even if the
      // m.mentions metadata is absent (legacy senders / ticket bodies).
      if (mentionsInclude(text, myId)) return true
      const names = this.myNames()
      if (!names.length) return false
      const re = new RegExp('(^|[^\\w@])@?(' + names.map(reEsc).join('|') + ')(?![\\w])', 'i')
      return re.test(String(text || ''))
    },
    highlight(body: string): string {
      const escaped = escapeHtml(body)
      // Linkify first (behind control-char placeholders, so mention marking
      // can't tear a URL apart). Same-origin links — ticket links — stay in
      // the SPA: a hash change navigates without a reload.
      const SOH = String.fromCharCode(1) // can't occur in escaped chat text
      const links: string[] = []
      let s = escaped.replace(/https?:\/\/[^\s<]+/g, (m) => {
        const external = !m.startsWith(escapeHtml(location.origin))
        links.push(`<a href="${m}" class="chat-link"${external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${m}</a>`)
        return SOH + (links.length - 1) + SOH
      })
      const names = this.myNames()
      if (names.length) {
        const re = new RegExp('(@?(?:' + names.map((n) => reEsc(escapeHtml(n))).join('|') + '))(?![\\w])', 'gi')
        s = s.replace(re, '<mark class="chat-mention">$1</mark>')
      }
      return s.replace(new RegExp(SOH + '(\\d+)' + SOH, 'g'), (_, i: string) => links[Number(i)])
    },

    /* ---- helpers ---- */
    nameOf(mxid: string): string {
      return useFloor().people[mxid]?.short || String(mxid || '').replace(/^@/, '').split(':')[0] || 'Someone'
    },
    slugOf(roomId: string): string | null {
      return useFloor().slugOf(roomId)
    },
    unreadFloor(roomId: string): number {
      return roomId in this.lastRead ? this.lastRead[roomId] : this.floorTs
    },

    pushInbox(item: Omit<InboxItem, 'id' | 'read'>): InboxItem {
      const full: InboxItem = { ...item, id: 'nb' + ++inboxSeq, read: false }
      if (!full.slug && full.roomId) full.slug = this.slugOf(full.roomId)
      this.inbox.unshift(full)
      if (this.inbox.length > 60) this.inbox.length = 60
      return full
    },

    persistRead() {
      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(() => {
        matrix.setAccountData(READ_ACCOUNT, { rooms: this.lastRead }).catch(() => {})
      }, 700)
    },
    markRead(roomId: string) {
      if (!roomId) return
      this.unread[roomId] = 0
      delete this.mentioned[roomId]
      this.lastRead[roomId] = Date.now()
      this.inbox.forEach((it) => { if (it.roomId === roomId) it.read = true })
      this.persistRead()
    },
    setActiveChat(roomId: string | null) {
      this.activeChat = roomId
      if (roomId) this.markRead(roomId)
    },

    /* ---- ingest a new timeline message ---- */
    onMessage(roomId: string, ev: MatrixEvent) {
      if (!ev || ev.type !== 'm.room.message') return
      const id = ev.event_id
      if (id) { if (this.seen.has(id)) return; this.seen.add(id) }
      if (ev.sender === matrix.userId()) return
      const content = ev.content as RoomMessageContent
      const body = content?.body || ''
      const ts = ev.origin_server_ts || Date.now()
      if (ts <= this.unreadFloor(roomId)) return

      const mention = this.isMention(body, content)
      const openHere = this.activeChat === roomId

      if (openHere) {
        this.markRead(roomId)
      } else {
        this.unread[roomId] = (this.unread[roomId] || 0) + 1
        if (mention) this.mentioned[roomId] = true
      }

      // A mention rings a bell; a plain message you're not already reading gives
      // a near-subliminal tick. No sound for the room you're actively in.
      if (mention) playSfx('mention')
      else if (!openHere) playSfx('message')

      if (mention) {
        const slug = this.slugOf(roomId)
        this.pushInbox({ kind: 'mention', roomId, slug, from: ev.sender, fromName: this.nameOf(ev.sender || ''), text: body, ts })
        // Also file it in the durable, source-separated mentions timeline. No
        // re-alert (`false`) — the toast + chime below already fired.
        if (ev.event_id) {
          const room = slug ? useFloor().roomBySlug(slug) : undefined
          useMentions().record({
            id: ev.event_id, source: 'chat', from: ev.sender || '', fromName: this.nameOf(ev.sender || ''),
            text: body, ts, roomId, slug, roomName: room?.name,
          }, false)
        }
        if (!openHere && !usePresence().isDnd) {
          useToasts().push({
            icon: 'note', color: 'var(--gold-bright)',
            title: `${this.nameOf(ev.sender || '')} mentioned you`,
            text: trim(body), action: slug ? 'Open' : undefined, ms: 6500,
            onAction: slug ? () => window.location.assign('#/room/' + slug) : undefined,
          })
        }
      }
    },

    /* ---- inbound to-device signal ---- */
    onSignal(ev: MatrixEvent) {
      if (!ev || !ev.type) return
      const c = ev.content as Record<string, any>
      const from = (c.from as string) || ev.sender || ''
      // Prefer a real sent name; otherwise resolve from the floor.
      const sent = c.name as string | undefined
      const name = sent && sent !== 'Someone' ? sent : this.nameOf(from)
      const ts = (c.ts as number) || Date.now()
      const toasts = useToasts()

      if (ev.type === 'app.atrium.knock') {
        const item = this.pushInbox({ kind: 'knock', from, fromName: name, text: c.msg || 'got a moment?', ts })
        if (usePresence().isDnd) { this.queuedKnocks.push(item); return }
        playSfx('knock')
        toasts.push({
          icon: 'knock', title: `${name} is knocking`, text: c.msg || 'got a moment?',
          sticky: true, action: 'Accept', onAction: () => this.acceptKnock(item.id),
        })
        return
      }
      if (ev.type === 'app.atrium.knock.response') {
        if (!c.accepted) {
          toasts.push({ icon: 'moon', title: `${name} is heads-down`, text: 'Your knock is queued.' })
          return
        }
        playSfx('notify')
        // Accepted: join them in one click. Always resolve their LIVE location
        // at click time — the room in the response is only a fallback, in case
        // they moved between accepting and you clicking Join.
        if (c.slug) {
          this.pushInbox({ kind: 'grab', from, fromName: name, slug: c.slug, roomName: c.roomName, follow: true, text: `accepted — join them in ${c.roomName || 'their room'}`, ts })
          toasts.push({
            icon: 'check', color: 'var(--p-open)',
            title: `${name} accepted`, text: `Join them in ${c.roomName || 'their room'}`,
            sticky: true, action: 'Join', onAction: () => this.goToPerson(from, c.slug as string),
          })
        } else {
          this.pushInbox({ kind: 'grab', from, fromName: name, follow: true, text: 'accepted your knock — they’re not in a room yet', ts })
          toasts.push({
            icon: 'check', color: 'var(--p-open)',
            title: `${name} accepted 👍`, text: 'They’re not in a room — grab them or find them on the floor.', ms: 8000,
          })
        }
        return
      }
      if (ev.type === 'app.atrium.grab') {
        playSfx('notify')
        this.pushInbox({ kind: 'grab', from, fromName: name, slug: c.slug || null, roomName: c.roomName, text: `wants you in ${c.roomName || 'a room'}`, ts })
        toasts.push({
          icon: 'grab', title: `${name} wants to grab you`, text: `into ${c.roomName || 'a room'}`,
          sticky: true, action: 'Join', onAction: () => { if (c.slug) window.location.assign('#/room/' + c.slug) },
        })
        return
      }
    },

    /** Navigate to a person's live room, falling back to a known slug. */
    goToPerson(mxid: string, fallbackSlug?: string | null) {
      const floor = useFloor()
      const live = floor.rooms.find((r) => r.occupants.includes(mxid))
      const target = live?.id || fallbackSlug
      if (target) {
        window.location.assign('#/room/' + target)
      } else {
        useToasts().push({
          icon: 'door', title: `${this.nameOf(mxid)} isn’t in a room right now`,
          text: 'Find them on the floor, or grab them when they surface.', ms: 6000,
        })
      }
    },

    /* ---- protocol-level room knocks (team rooms) ---- */
    onMemberEvent(roomId: string, ev: MatrixEvent) {
      const c = ev.content as Record<string, any> | undefined
      const knocker = ev.state_key
      if (!knocker || knocker === matrix.userId()) return
      if (c?.membership !== 'knock') {
        // Any newer membership supersedes a pending knock from this person —
        // they were let in, gave up, or were turned away. Retire the prompt.
        this.inbox.forEach((it) => {
          if (it.kind === 'roomknock' && it.from === knocker && it.roomId === roomId && !it.read) {
            it.read = true
            if (c?.membership === 'invite' || c?.membership === 'join') it.accepted = true
          }
        })
        this.queuedKnocks = this.queuedKnocks.filter((it) => !(it.kind === 'roomknock' && it.from === knocker && it.roomId === roomId))
        return
      }
      const id = ev.event_id
      if (id) { if (this.seen.has(id)) return; this.seen.add(id) }
      const floor = useFloor()
      const slug = floor.slugOf(roomId)
      const room = slug ? floor.roomBySlug(slug) : null
      const roomName = room?.name || 'a room'
      const name = this.nameOf(knocker)
      const item = this.pushInbox({
        kind: 'roomknock', from: knocker, fromName: name, roomId, slug, roomName,
        text: c?.reason || `wants to join ${roomName}`, ts: ev.origin_server_ts || Date.now(),
      })
      if (usePresence().isDnd) { this.queuedKnocks.push(item); return }
      playSfx('knock')
      useToasts().push({
        icon: 'knock', color: room?.accent,
        title: `${name} is knocking on ${roomName}`, text: 'Let them in?',
        sticky: true, action: 'Let in', onAction: () => this.admitKnock(item.id),
      })
    },

    /** Letting a knocker in = inviting them (Matrix answers a knock with an invite). */
    async admitKnock(id: string) {
      const item = this.inbox.find((it) => it.id === id)
      if (!item?.from || !item.roomId) return
      const from = item.from
      // The knock may have been answered or retracted since this prompt was
      // shown — only invite if they are still knocking right now.
      try {
        const cur = await matrix.stateEvent<{ membership?: string }>(item.roomId, 'm.room.member', from)
        if (cur?.membership !== 'knock') {
          item.read = true
          if (cur?.membership === 'invite' || cur?.membership === 'join') item.accepted = true
          useToasts().push({ icon: 'door', title: 'Already handled', text: `${item.fromName || from} isn’t knocking anymore.`, ms: 5000 })
          return
        }
      } catch { /* state lookup failed — fall through and let the invite try */ }
      matrix.invite(item.roomId, from).then(() => {
        item.read = true
        item.accepted = true
        useToasts().push({ icon: 'door', color: 'var(--p-open)', title: 'Door opened', text: `${item.fromName || from} can come in now.`, ms: 5000 })
      }).catch((e) => {
        useToasts().push({ icon: 'x', color: 'var(--danger)', title: 'Couldn’t let them in', text: e instanceof Error ? e.message : undefined })
      })
    },

    acceptKnock(id: string) {
      const item = this.inbox.find((it) => it.id === id)
      if (!item || !item.from) return
      const floor = useFloor()
      const myId = matrix.userId()
      const myName = floor.you?.name || 'You'
      // Tell the knocker where the open door is — accepting means "come in".
      const slug = floor.activeSlug
      const room = slug ? floor.roomBySlug(slug) : null
      matrix.sendToDevice('app.atrium.knock.response', {
        [item.from]: { '*': {
          from: myId, name: myName, accepted: true, ts: Date.now(),
          slug: slug || null, roomName: room?.name || null,
        } },
      }).catch((e) => console.warn('[notify] accept knock failed', e))
      item.read = true
      item.accepted = true
      useToasts().push({
        icon: 'door', color: 'var(--p-open)',
        title: room ? 'Door’s open' : 'Knock accepted',
        text: room
          ? `${item.fromName || 'They'} can join you in ${room.name}.`
          : `Enter a room so ${item.fromName || 'they'} can join you — or grab them.`,
        ms: 6500,
      })
    },

    /* ---- inbox panel ---- */
    open() { this.inboxOpen = true },
    close() { this.inboxOpen = false },
    toggle() { this.inboxOpen = !this.inboxOpen },
    markAllRead() { this.inbox.forEach((it) => { it.read = true }) },
    ago(ts: number): string { return relativeAgo(ts) },
  },
})

function trim(t: string): string {
  const s = String(t || '').replace(/\s+/g, ' ').trim()
  return s.length > 90 ? s.slice(0, 88) + '…' : s
}
